import { useState, useEffect } from 'react';
import { useUpdateProgramContacts } from '../../../hooks/useProgramContacts';
import type { ProgramContactsData } from '../../../services/rpc';
import styles from './ContactsPanel.module.css';

export interface ContactsFormProps {
  programId: string;
  contactsData: ProgramContactsData;
}

export function ContactsForm({ programId, contactsData }: ContactsFormProps) {
  const [formData, setFormData] = useState({
    contact_name: contactsData.contact_name || '',
    contact_email: contactsData.contact_email || '',
    contact_phone: contactsData.contact_phone || '',
    physical_address: contactsData.physical_address || '',
    contact_notes: contactsData.contact_notes || ''
  });

  const [isDirty, setIsDirty] = useState(false);

  const updateMutation = useUpdateProgramContacts({
    onSuccess: () => {
      setIsDirty(false);
    }
  });

  // Reset form when contactsData changes (e.g., after QC run)
  useEffect(() => {
    setFormData({
      contact_name: contactsData.contact_name || '',
      contact_email: contactsData.contact_email || '',
      contact_phone: contactsData.contact_phone || '',
      physical_address: contactsData.physical_address || '',
      contact_notes: contactsData.contact_notes || ''
    });
    setIsDirty(false);
  }, [contactsData]);

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleSave = () => {
    updateMutation.mutate({
      program_id: programId,
      contact_name: formData.contact_name || null,
      contact_email: formData.contact_email || null,
      contact_phone: formData.contact_phone || null,
      physical_address: formData.physical_address || null,
      contact_notes: formData.contact_notes || null
    });
  };

  const handleReset = () => {
    setFormData({
      contact_name: contactsData.contact_name || '',
      contact_email: contactsData.contact_email || '',
      contact_phone: contactsData.contact_phone || '',
      physical_address: contactsData.physical_address || '',
      contact_notes: contactsData.contact_notes || ''
    });
    setIsDirty(false);
  };

  return (
    <div className={styles.card}>
      <h2 className={styles.cardHeader}>Contact Information</h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <label htmlFor="contact_name" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
            Contact Name
          </label>
          <input
            id="contact_name"
            type="text"
            value={formData.contact_name}
            onChange={(e) => handleChange('contact_name', e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem'
            }}
            placeholder="Contact person or organization name"
          />
        </div>

        <div>
          <label htmlFor="contact_email" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
            Contact Email
          </label>
          <input
            id="contact_email"
            type="email"
            value={formData.contact_email}
            onChange={(e) => handleChange('contact_email', e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem'
            }}
            placeholder="email@example.com"
          />
        </div>

        <div>
          <label htmlFor="contact_phone" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
            Contact Phone
          </label>
          <input
            id="contact_phone"
            type="tel"
            value={formData.contact_phone}
            onChange={(e) => handleChange('contact_phone', e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem'
            }}
            placeholder="+1 (555) 123-4567"
          />
        </div>

        <div>
          <label htmlFor="physical_address" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
            Physical Address
          </label>
          <textarea
            id="physical_address"
            value={formData.physical_address}
            onChange={(e) => handleChange('physical_address', e.target.value)}
            rows={3}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
            placeholder="Full mailing address"
          />
        </div>

        <div>
          <label htmlFor="contact_notes" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
            Contact Notes
          </label>
          <textarea
            id="contact_notes"
            value={formData.contact_notes}
            onChange={(e) => handleChange('contact_notes', e.target.value)}
            rows={3}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
            placeholder="Additional notes about the contact"
          />
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
          <button
            onClick={handleSave}
            disabled={!isDirty || updateMutation.isPending}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: isDirty ? 'var(--color-brand, #1c8e1c)' : '#d1d5db',
              color: '#ffffff',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: isDirty ? 'pointer' : 'not-allowed',
              opacity: isDirty ? 1 : 0.6
            }}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Manual Edits'}
          </button>

          <button
            onClick={handleReset}
            disabled={!isDirty}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#ffffff',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: isDirty ? 'pointer' : 'not-allowed',
              opacity: isDirty ? 1 : 0.6
            }}
          >
            Reset to DB
          </button>
        </div>

        {updateMutation.isError && (
          <div className={styles.error}>
            Error saving: {updateMutation.error.message}
          </div>
        )}

        {updateMutation.isSuccess && (
          <div style={{ padding: '0.75rem', backgroundColor: '#ecfdf5', borderRadius: '0.375rem', fontSize: '0.875rem', color: '#059669' }}>
            Changes saved successfully!
          </div>
        )}
      </div>
    </div>
  );
}

export default ContactsForm;
